import { PERMISSION } from "@/hooks/useCapabilities";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Checkbox from "@/components/Checkbox";
import { useMutation } from "@apollo/client";
import Button from "@/components/Button";
import classNames from "classnames";
import Banner from "@/components/Banner";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import useCapabilities from "@/hooks/useCapabilities";
import { JobPreferencesFormData } from "../job-preferences";
import { jobCategories, jobSearchStatuses } from "../_constants";
import { UPDATE_JOB_PREFERENCES_MUTATION } from "../queries";
import EditPreferencesCard from "./EditPreferencesCard";

interface EditableJobPreferencesProps {
  jobPreferencesData: {
    person: {
      id: string;
      jobPreferences: {
        id: string;
        contact: {
          id: string;
          firstName: string;
          lastName: string;
        };
        createdAt: Date | string;
        updatedAt: Date | string;
        interestInInternationalPositions: boolean;
        jobCategories: string[];
        jobCompensationMinimum: string;
        jobSearchStatus: string;
        locationPreferences: string[];
        openToSeasonJobs: boolean;
        optedInToJobSeekerEmails: boolean;
      };
    };
  };
}

export default function EditableJobPreferences({ jobPreferencesData }: EditableJobPreferencesProps) {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitValidationMessage, setSubmitValidationMessage] = useState<string | null>(null);

  const [updateJobPreferences] = useMutation(UPDATE_JOB_PREFERENCES_MUTATION, {
    onCompleted: (data) => {
      setIsSubmitting(false);
      if (data.patchJobPreferences.success) {
        showSnackbar("Job preferences updated");
        setEditingSection(null);
      } else {
        showErrorSnackbar(data.patchJobPreferences.message || "Failed to update job preferences");
      }
    },
    onError: (error) => {
      showErrorSnackbar(error.message || "Failed to update job preferences");
      setIsSubmitting(false);
    }
  });

  const form = useForm({
    defaultValues: {
      jobSearchStatus: jobPreferencesData?.person?.jobPreferences?.jobSearchStatus || "not_looking",
      optedInToJobSeekerEmails: jobPreferencesData?.person?.jobPreferences?.optedInToJobSeekerEmails || false,
      jobCompensationMinimum: jobPreferencesData?.person?.jobPreferences?.jobCompensationMinimum?.toString() || "0",
      openToSeasonJobs: jobPreferencesData?.person?.jobPreferences?.openToSeasonJobs || false,
      locationPreferences: jobPreferencesData?.person?.jobPreferences?.locationPreferences || [],
      interestInInternationalPositions:
        jobPreferencesData?.person?.jobPreferences?.interestInInternationalPositions || false,
      jobCategories: jobPreferencesData?.person?.jobPreferences?.jobCategories || []
    }
  });

  const { reset } = form;

  const jobSearchStatusLabel =
    jobSearchStatuses.find((status) => status.value === form.watch("jobSearchStatus"))?.label ||
    "Not looking for a new position";

  const handleSubmit = async (formData: JobPreferencesFormData) => {
    setIsSubmitting(true);

    if (!jobPreferencesData?.person?.jobPreferences?.id) {
      setSubmitValidationMessage("Job preferences ID is required");
      setIsSubmitting(false);
      return;
    }

    setSubmitValidationMessage(null);

    const input = {
      id: jobPreferencesData?.person?.jobPreferences?.id,
      jobSearchStatus: formData.jobSearchStatus,
      optedInToJobSeekerEmails: formData.optedInToJobSeekerEmails,
      jobCompensationMinimum: formData.jobCompensationMinimum ? parseInt(formData.jobCompensationMinimum) : 0,
      openToSeasonJobs: formData.openToSeasonJobs,
      locationPreferences: formData.locationPreferences,
      interestInInternationalPositions: formData.interestInInternationalPositions,
      jobCategories: formData.jobCategories
    };

    await updateJobPreferences({ variables: { input } });
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setSubmitValidationMessage(null);
    // Reset form to original values from data
    if (jobPreferencesData) {
      reset({
        jobSearchStatus: jobPreferencesData.person.jobPreferences?.jobSearchStatus,
        optedInToJobSeekerEmails: jobPreferencesData.person.jobPreferences?.optedInToJobSeekerEmails,
        jobCompensationMinimum: jobPreferencesData.person.jobPreferences?.jobCompensationMinimum?.toString() || "0",
        openToSeasonJobs: jobPreferencesData.person.jobPreferences?.openToSeasonJobs,
        locationPreferences: jobPreferencesData.person.jobPreferences?.locationPreferences,
        interestInInternationalPositions: jobPreferencesData.person.jobPreferences?.interestInInternationalPositions,
        jobCategories: jobPreferencesData.person.jobPreferences?.jobCategories
      });
    }
  };

  useEffect(() => {
    if (jobPreferencesData) {
      reset({
        jobSearchStatus: jobPreferencesData.person.jobPreferences?.jobSearchStatus,
        optedInToJobSeekerEmails: jobPreferencesData.person.jobPreferences?.optedInToJobSeekerEmails,
        jobCompensationMinimum: jobPreferencesData.person.jobPreferences?.jobCompensationMinimum?.toString() || "0",
        openToSeasonJobs: jobPreferencesData.person.jobPreferences?.openToSeasonJobs,
        locationPreferences: jobPreferencesData.person.jobPreferences?.locationPreferences,
        interestInInternationalPositions: jobPreferencesData.person.jobPreferences?.interestInInternationalPositions,
        jobCategories: jobPreferencesData.person.jobPreferences?.jobCategories
      });
    }
  }, [jobPreferencesData, reset]);

  const { hasPermission } = useCapabilities();
  const canEdit = hasPermission(PERMISSION.manageCareerPreferences);

  return (
    <div className="flex flex-col gap-4 mb-4">
      {submitValidationMessage && (
        <Banner
          inline
          variant="warn"
          description={submitValidationMessage}
          closable
          onClose={() => setSubmitValidationMessage(null)}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MultiSectionDetailsCard
          className="!h-fit lg:col-span-1"
          editingSection={editingSection}
          setEditingSection={canEdit ? setEditingSection : undefined}
          sections={[
            {
              heading: "Preferences",
              options: [
                {
                  iconName: "business_center",
                  overline: "Job Search Status",
                  headline: jobSearchStatusLabel
                },
                {
                  iconName: "mail",
                  overline: "Email Notifications",
                  headline: form.watch("optedInToJobSeekerEmails") ? "Opted In" : "Opted Out"
                },
                {
                  iconName: "attach_money",
                  overline: "Compensation Minimum",
                  headline: (() => {
                    const value = form.watch("jobCompensationMinimum");
                    if (!value || value === "0" || Number(value) === 0) {
                      return "No Minimum";
                    }
                    return `$${Number(value).toLocaleString("en-US")}`;
                  })()
                },
                {
                  iconName: "event_available",
                  overline: "Open to Seasonal Positions",
                  headline: form.watch("openToSeasonJobs") ? "Yes" : "No"
                },
                {
                  iconName: "location_on",
                  overline: "Location Preferences",
                  headline: form.watch("locationPreferences")?.join(", ") || "None selected"
                },
                {
                  iconName: "location_on",
                  overline: "International Interest",
                  headline: form.watch("interestInInternationalPositions") ? "Yes" : "No"
                }
              ],
              ...(canEdit && {
                renderEdit: () => (
                  <EditPreferencesCard
                    form={form}
                    onSubmit={handleSubmit}
                    onCancel={handleCancelEdit}
                    isSubmitting={isSubmitting}
                  />
                )
              })
            }
          ]}
        />
        <div className="flex flex-col gap-4 lg:col-span-2">
          <MultiSectionDetailsCard
            editingSection={editingSection}
            setEditingSection={canEdit ? setEditingSection : undefined}
            sections={[
              {
                heading: "Job Categories",
                options: [
                  ...jobCategories.map((category: string) => ({
                    headline: category,
                    leading: (
                      <Checkbox
                        name={`jobCategories.${category}`}
                        checked={form.watch("jobCategories")?.includes(category)}
                        disabled={editingSection !== "Job Categories"}
                        onChange={() => {
                          const currentCategories = form.watch("jobCategories") || [];
                          const newCategories = currentCategories.includes(category)
                            ? currentCategories.filter((c: string) => c !== category)
                            : [...currentCategories, category];
                          form.setValue("jobCategories", newCategories);
                        }}
                      />
                    )
                  }))
                ],
                ...(canEdit && {
                  renderEdit: () => (
                    <div className="space-y-6 w-full">
                      <div className="space-y-4">
                        {jobCategories.map((category: string) => (
                          <div key={category} className="items-center flex space-x-2 w-full h-[40px]">
                            <Checkbox
                              name={`jobCategories.${category}`}
                              checked={form.watch("jobCategories")?.includes(category)}
                              onChange={() => {
                                const currentCategories = form.watch("jobCategories") || [];
                                const newCategories = currentCategories.includes(category)
                                  ? currentCategories.filter((c: string) => c !== category)
                                  : [...currentCategories, category];
                                form.setValue("jobCategories", newCategories);
                              }}
                            />
                            <span className="break-words text-sys-brand-on-surface text-body-large">{category}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outlined" disabled={isSubmitting} onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          disabled={isSubmitting}
                          icon={isSubmitting ? "progress_activity" : undefined}
                          iconClassName={classNames(isSubmitting && "animate-spin w-6 h-6 !block")}
                          variant="filled"
                          onClick={() => handleSubmit(form.getValues())}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  )
                })
              }
            ]}
          />
        </div>
      </div>
    </div>
  );
}
